import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import ImageWithFallback from './ImageWithFallback';
import './Header.css';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleWorkClick = (e) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/#work');
      setTimeout(() => {
        const element = document.getElementById('work');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById('work');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/', label: 'Work', onClick: handleWorkClick, isHash: true },
    { path: '/services', label: 'Solutions' },
    { path: '/about', label: 'About' },
    { path: '/news', label: 'News' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <motion.header 
      className={`header ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div className="header-container">
        <Link to="/" className="logo">
          <ImageWithFallback 
            src="/assets/img/logo.png" 
            alt="Team FullStack"
            priority={true}
            fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='50'%3E%3Ctext x='10' y='35' font-family='Arial' font-size='24' font-weight='bold'%3ETeam FullStack%3C/text%3E%3C/svg%3E"
          />
        </Link>

        <nav className="nav-desktop">
          {navItems.map((item, index) => {
            if (item.isHash) {
              return (
                <a
                  key={index}
                  href="#work"
                  onClick={item.onClick}
                  className="nav-link"
                >
                  {item.label}
                </a>
              );
            }
            return (
                    <Link 
                key={index}
                      to={item.path} 
                className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                    >
                {item.label}
                    </Link>
            );
          })}
        </nav>

        <div className="header-actions">
          <a href="https://www.linkedin.com/company/team-fullstack/" className="social-link" target="_blank" rel="noopener noreferrer">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
            </svg>
          </a>
          <Link to="/contact" className="btn-contact">
            Contact us →
          </Link>
        </div>

        <button
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.nav
            className="nav-mobile"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            {navItems.map((item, index) => {
              if (item.isHash) {
                return (
                  <a
                    key={index}
                    href="#work"
                    onClick={(e) => {
                      e.preventDefault();
                      item.onClick(e);
                      setIsMobileMenuOpen(false);
                    }}
                    className="nav-link-mobile"
                  >
                    {item.label}
                  </a>
                );
              }
              return (
                                <Link 
                  key={index}
                        to={item.path} 
                  className="nav-link-mobile"
                  onClick={() => setIsMobileMenuOpen(false)}
                      >
                  {item.label}
                      </Link>
              );
            })}
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
